import type { HTMLAttributes } from "react";

export type StageBadgeStage = "local" | "dev" | "prod";

export interface StageBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Stage identifier driving the color + label. `prod` renders `null`
   * (prod is the silent default — no badge), so consumers can mount
   * `<StageBadge stage={stage} />` unconditionally without a wrapping
   * `stage !== "prod" && …` guard.
   */
  stage: StageBadgeStage;
  /**
   * Override the displayed label. Defaults to the canonical mapping
   * (`local` → `LOCAL`, `dev` → `DEV`). Useful in stories / tests that
   * need to assert a custom label without coupling to the default
   * mapping.
   */
  label?: string;
  /**
   * Opt-in solid fill variant. The default `false` keeps the subtle
   * 15%-fill / 40%-border tint that reads well on dark popup chrome.
   * Pass `true` on surfaces that sit on top of the brand accent (the
   * dock restore FAB, the dock panel header) where the subtle tint
   * washes out against the teal background. Solid uses full-opacity
   * fill + white-ish label for high contrast.
   */
  solid?: boolean;
}

/**
 * StageBadge — small color pill marking the active build stage in
 * the web shell + extension popup headers.
 *
 * Pair with `getStageLabel()` from `@nu-assist/shared-fe` (the stage
 * resolver lives there because it reads `import.meta.env.VITE_APP_STAGE`;
 * keeping this primitive env-free preserves the "pure design system"
 * contract for `@nu-assist/ui`).
 *
 * Variants:
 *   - local → green (LOCAL is a safe affordance — your machine).
 *   - dev   → orange (matches PR #114's per-stage toolbar icon family).
 *   - prod  → renders `null` (prod is the silent default).
 *
 * Shape matches the other status pills (`NavStatusBadge`, `NewBadge`,
 * `ContextBadge`): pill radius, `text-micro` (10px / 600 weight),
 * `tracking-[0.02em]`, `whitespace-nowrap`. No inline styles, no
 * arbitrary text sizes, only Tailwind utilities — per the workspace
 * "no inline styles" rule.
 *
 * The green/orange families use Tailwind palette colors (`green-500`,
 * `orange-500`) rather than design-system tokens because:
 *   - `bg-warning` (the amber/warning token) is already claimed by
 *     `NavStatusBadge`'s coming-soon variant — reusing it would visually
 *     collide.
 *   - The design system has no first-class "stage" color token yet; if
 *     stage-aware styling spreads elsewhere we can promote
 *     `--stage-local` / `--stage-dev` in a follow-up.
 */
export function StageBadge({
  stage,
  label,
  solid = false,
  className,
  ...rest
}: StageBadgeProps) {
  if (stage === "prod") return null;

  const defaultLabel = stage === "local" ? "LOCAL" : "DEV";
  const displayLabel = label ?? defaultLabel;

  const subtleVariants: Record<Exclude<StageBadgeStage, "prod">, string> = {
    local: "bg-green-500/15 border border-green-500/40 text-green-400",
    dev: "bg-orange-500/15 border border-orange-500/40 text-orange-400",
  };
  // Solid: full-opacity fill + white label. Used on surfaces where the
  // subtle tint washes out against the brand accent (FAB, panel header).
  const solidVariants: Record<Exclude<StageBadgeStage, "prod">, string> = {
    local: "bg-green-500 border border-green-600 text-white",
    dev: "bg-orange-500 border border-orange-600 text-white",
  };
  const variants = solid ? solidVariants : subtleVariants;

  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 text-micro font-semibold tracking-[0.02em] whitespace-nowrap";
  const classes = [base, variants[stage], className].filter(Boolean).join(" ");

  return (
    <span {...rest} className={classes} data-stage={stage} data-solid={solid}>
      {displayLabel}
    </span>
  );
}
